import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../utils/GlobalAPI';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Foooter';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await axios.get(`${API_ENDPOINTS.BLOGS}/${id}`);
        setBlog(res.data);
      } catch (err) {
        console.error('Error loading blog:', err);
        setError('Blog not found');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBlog();
    }
  }, [id]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading blog post...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Blog Not Found</h2>
            <p className="text-gray-600 mb-6">{error || 'The blog post you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/blog')}
              className="inline-block px-6 py-3 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Back to Blog
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-16 sm:py-20 px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-8 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Blog
            </button>

            {blog.type === 'Featured' && (
              <span className="inline-block bg-gradient-to-r from-orange-300 to-pink-300 text-white text-xs px-4 py-2 rounded-full font-semibold mb-6">
                FEATURED
              </span>
            )}

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 tracking-tight mb-6">
              <span className="font-semibold">{blog.heading}</span>
            </h1>

            <p className="text-xl sm:text-2xl font-light text-gray-600 leading-relaxed mb-8">
              {blog.subtext}
            </p>

            <div className="flex items-center text-sm text-gray-500">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {new Date(blog.createdAt || Date.now()).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {blog.image && (
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 -mt-12 mb-12">
            <img
              src={blog.image}
              alt={blog.heading}
              className="w-full h-64 sm:h-96 lg:h-[500px] object-cover rounded-3xl shadow-2xl border border-gray-200"
            />
          </div>
        )}

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 pb-20">
          <div className="prose prose-lg max-w-none">
            <div className="text-gray-700 leading-relaxed whitespace-pre-line text-lg">
              {blog.content.split('\n').map((paragraph, idx) => (
                paragraph.trim() ? (
                  <p key={idx} className="mb-6">
                    {paragraph}
                  </p>
                ) : (
                  <br key={idx} />
                )
              ))}
            </div>
          </div>

          {/* Back to Blog Button */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to All Posts
            </button>
          </div>
        </div>
      </article>
      <Footer />
    </>
  );
};

export default BlogDetail;

